import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { AcornsIcon, Icon } from '@acorns/icons'
import { useAnalyticsClient } from '@acorns/web-utils'
import styled from 'styled-components'

import { routes } from 'router/routes'
import {
  BANKING_HUB_ICON,
  BOTTOM_BAR_CONTAINER,
  EARN_ICON,
  GROW_ICON,
  HOME_ICON,
  INVEST_HUB_ICON,
} from 'utils/testing/testIDs'

import { onBottomNavButtonTapped } from './events'

const Container = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 30px;

  width: 100%;
  height: 72px;

  background: ${(props) => props.theme.colors.white};
  box-shadow: 0px -1px 0px rgba(25, 25, 25, 0.04);

  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  z-index: 500;
`

const NavIconContainer = styled.div`
  width: 24px;
  height: 24px;
  flex: none;
  order: 0;
  flex-grow: 0;
`

const StyledAcornsIcon = styled(AcornsIcon)<{ isActive?: boolean }>`
  position: relative;
  width: 24px;
  height: 24px;
  left: calc(50% - 24px / 2);
  top: calc(50% - 24px / 2);
  ${(props) => !props.isActive && 'opacity: 0.4;'};
`

const Product = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 48px;
  height: 48px;
  align-items: center;
`

const ProductTitle = styled.div<{ isActive?: boolean }>`
  font-weight: 600;
  font-size: 10px;
  line-height: 14px;
  margin-top: 6px;
  ${(props) => !props.isActive && 'opacity: 0.4;'};
`

export enum BaseRoutes {
  PRESENT = 'present',
  INVESTING = 'investing-hub',
  INVEST = 'invest',
  LATER = 'later',
  EARLY = 'early',
  BANKING = 'banking-hub',
  CHECKING = 'spend',
  GROW = 'grow',
  EARN = 'earn',
  LEARN = 'learn-hub',
}

const BottomBar = () => {
  const productList = [
    {
      routeName: [BaseRoutes.PRESENT],
      title: 'Home',
      icon: AcornsIcon.Icon.UtilityHomeFv,
      url: routes.present,
      testID: HOME_ICON,
      className: BaseRoutes.PRESENT,
    },
    {
      routeName: [
        BaseRoutes.INVESTING,
        BaseRoutes.INVEST,
        BaseRoutes.LATER,
        BaseRoutes.EARLY,
      ],
      title: 'Investing',
      icon: AcornsIcon.Icon.UtilityStocksFv,
      url: routes.investingHub.index,
      testID: INVEST_HUB_ICON,
      className: BaseRoutes.INVESTING,
    },
    {
      routeName: [BaseRoutes.BANKING, BaseRoutes.CHECKING],
      title: 'Banking',
      icon: AcornsIcon.Icon.UtilityBankFv,
      url: routes.bankingHub.index,
      testID: BANKING_HUB_ICON,
      className: BaseRoutes.BANKING,
    },
    {
      routeName: [BaseRoutes.LEARN],
      title: 'Learning',
      icon: AcornsIcon.Icon.ProductGrowFv,
      url: routes.learn.index,
      testID: GROW_ICON,
      className: BaseRoutes.LEARN,
    },
    {
      routeName: [BaseRoutes.EARN],
      title: 'Earning',
      icon: AcornsIcon.Icon.ProductEarnFv,
      url: routes.earn.index,
      testID: EARN_ICON,
      className: BaseRoutes.EARN,
    },
  ]

  const history = useHistory()
  const location = useLocation()
  const analytics = useAnalyticsClient()
  const pathName = location.pathname.split('/').slice(1)[0]

  const isActive = (routeName: string[]) => {
    return routeName.map((route) => pathName.includes(route)).includes(true)
  }
  const handleClick = (url: string, product: string, index: number) => {
    analytics.track('Button Tapped', onBottomNavButtonTapped(product, index))
    history.push(url, { referrer: location.pathname })
  }

  const renderIcon = (routeName: string[], icon: Icon, className: string) => {
    return (
      <NavIconContainer>
        <StyledAcornsIcon
          icon={icon}
          isActive={isActive(routeName)}
          className={className}
        />
      </NavIconContainer>
    )
  }

  return (
    <Container
      data-testid={BOTTOM_BAR_CONTAINER}
      className="bottom-bar-container"
    >
      {productList.map((product, index) => (
        <Product
          key={product.icon}
          data-testid={product.testID}
          onClick={() => handleClick(product.url, product.className, index)}
        >
          {renderIcon(product.routeName, product.icon, product.className)}
          <ProductTitle isActive={isActive(product.routeName)}>
            {product.title}
          </ProductTitle>
        </Product>
      ))}
    </Container>
  )
}
export default BottomBar
